import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import { userManager } from '../oidc';
import { useAuth } from '../AuthProvider';
import { colors, fonts } from '../../styles/theme';
import LoadingState from '../../components/states/LoadingState';

/**
 * Callback-Seite nach erfolgreichem Logout.
 * Räumt die lokale OIDC-Session auf und bietet erneutes Anmelden an.
 */
const SignoutCallbackPage: React.FC = () => {
  const { login } = useAuth();
  const [isCleaningUp, setIsCleaningUp] = useState(true);
  const cleanupProcessed = useRef(false);

  useEffect(() => {
    if (cleanupProcessed.current) return;
    cleanupProcessed.current = true;

    userManager.removeUser().finally(() => {
      setIsCleaningUp(false);
    });
  }, []);

  const handleLogin = async () => {
    await login();
  };

  if (isCleaningUp) {
    return (
      <LoadingState
        fullScreen={true}
        message="Abmeldung wird verarbeitet..."
        subtitle="Bitte warten Sie einen Moment."
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${colors.background} 0%, ${colors.primary} 100%)`,
        padding: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          padding: 4,
          width: '100%',
          maxWidth: 400,
          borderRadius: 3,
          background: colors.glass.backgroundStrong,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${colors.glass.borderSubtle}`,
          textAlign: 'center',
        }}
      >
        <Box
          component="img"
          src="/logo.png"
          alt="strømcraft Logo"
          sx={{ height: 60, width: 'auto', mb: 2 }}
        />

        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            fontFamily: fonts.monospace,
            textTransform: 'lowercase',
            color: colors.text.primary,
            mb: 1,
          }}
        >
          strømcraft
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, mb: 3 }}
        >
          Du wurdest erfolgreich abgemeldet.
        </Typography>

        <Button
          fullWidth
          variant="contained"
          onClick={handleLogin}
          startIcon={<SecurityIcon />}
          sx={{
            py: 2,
            borderRadius: 2,
            background: `linear-gradient(45deg, ${colors.primary} 30%, ${colors.secondary} 90%)`,
            boxShadow: '0 3px 5px 2px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              background: `linear-gradient(45deg, ${colors.secondary} 30%, ${colors.primary} 90%)`,
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 8px 2px rgba(0, 0, 0, 0.15)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Erneut anmelden
        </Button>
      </Paper>
    </Box>
  );
};

export default SignoutCallbackPage;
